import { Axios } from 'shared/utilities/Axios'
import {
  MFA_PASSKEY_AUTH_BEGIN_URL,
  MFA_PASSKEY_AUTH_COMPLETE_URL,
  MFA_PASSKEY_REGISTER_BEGIN_URL,
  MFA_PASSKEY_REGISTER_COMPLETE_URL,
  MFA_PASSKEYS_URL,
  MFA_STATUS_URL,
  MFA_TOTP_SETUP_BEGIN_URL,
  MFA_TOTP_SETUP_CONFIRM_URL,
  MFA_TOTP_VERIFY_URL,
} from 'shared/constants/urls'
import {
  assertionToPayload,
  credentialToPayload,
  parseCreationOptions,
  parseRequestOptions,
} from 'shared/utilities/webauthnUtils'

export const MFA_LOGIN_STEP = {
  COMPLETE: 'complete',
  MFA_VERIFY: 'mfa_verify',
  MFA_ENROLL: 'mfa_enroll',
}

export function parseLoginResponse(data) {
  if (data.access && data.refresh) {
    return { step: MFA_LOGIN_STEP.COMPLETE, tokens: data, grace: data.mfa_setup_recommended }
  }
  if (data.mfa_required) {
    return {
      step: MFA_LOGIN_STEP.MFA_VERIFY,
      challengeId: data.challenge_id,
      methods: data.methods || [],
    }
  }
  if (data.mfa_enrollment_required) {
    return {
      step: MFA_LOGIN_STEP.MFA_ENROLL,
      enrollmentToken: data.enrollment_token,
      detail: data.detail,
      enforcementAt: data.mfa_enforcement_starts_at,
    }
  }
  return { step: MFA_LOGIN_STEP.COMPLETE, tokens: data }
}

export function verifyLoginTotp(challengeId, code) {
  return Axios.post(MFA_TOTP_VERIFY_URL, { challenge_id: challengeId, code })
}

export async function authenticateLoginPasskey(challengeId) {
  const beginResp = await Axios.post(MFA_PASSKEY_AUTH_BEGIN_URL, { challenge_id: challengeId })
  const credential = await navigator.credentials.get({
    publicKey: parseRequestOptions(beginResp.data.publicKey),
  })
  return Axios.post(MFA_PASSKEY_AUTH_COMPLETE_URL, {
    challenge_id: challengeId,
    credential: assertionToPayload(credential),
  })
}

export function beginTotpEnrollment({ enrollmentToken, accessToken } = {}) {
  const headers = accessToken ? { authorization: `Bearer ${accessToken}` } : {}
  const body = enrollmentToken ? { enrollment_token: enrollmentToken } : {}
  return Axios.post(MFA_TOTP_SETUP_BEGIN_URL, body, { headers })
}

export function confirmTotpEnrollment(code, { enrollmentToken, accessToken } = {}) {
  const headers = accessToken ? { authorization: `Bearer ${accessToken}` } : {}
  const body = { code }
  if (enrollmentToken) {
    body.enrollment_token = enrollmentToken
  }
  return Axios.post(MFA_TOTP_SETUP_CONFIRM_URL, body, { headers })
}

export async function registerPasskey(name, { enrollmentToken, accessToken } = {}) {
  const headers = accessToken ? { authorization: `Bearer ${accessToken}` } : {}
  const body = enrollmentToken ? { enrollment_token: enrollmentToken } : {}
  const beginResp = await Axios.post(MFA_PASSKEY_REGISTER_BEGIN_URL, body, { headers })
  const credential = await navigator.credentials.create({
    publicKey: parseCreationOptions(beginResp.data.publicKey),
  })
  return Axios.post(
    MFA_PASSKEY_REGISTER_COMPLETE_URL,
    { name: name || 'Passkey', credential: credentialToPayload(credential), ...body },
    { headers }
  )
}

export function fetchMfaStatus(accessToken) {
  return Axios.get(MFA_STATUS_URL, {
    headers: { authorization: `Bearer ${accessToken}` },
  })
}

export function fetchPasskeys(accessToken) {
  return Axios.get(MFA_PASSKEYS_URL, {
    headers: { authorization: `Bearer ${accessToken}` },
  })
}

export function renamePasskey(id, name, accessToken) {
  return Axios.patch(
    `${MFA_PASSKEYS_URL}${id}/`,
    { name },
    { headers: { authorization: `Bearer ${accessToken}` } }
  )
}

export function revokePasskey(id, accessToken) {
  return Axios.delete(`${MFA_PASSKEYS_URL}${id}/`, {
    headers: { authorization: `Bearer ${accessToken}` },
  })
}
